import { createRoute, Link, redirect } from "@tanstack/react-router";
import { Add01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, type CaseLocalePublish, type CaseSummary } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  localePublishBadgeVariant,
  localePublishTooltip,
} from "@/lib/publish-targets";
import { rootRoute } from "./root-route";

export const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: async () => {
    try {
      await api.me();
    } catch {
      throw redirect({
        to: "/login",
        search: { returnTo: "/", error: undefined },
      });
    }
  },
  component: CasesPage,
});

function localePublishesForCase(item: CaseSummary): CaseLocalePublish[] {
  if (item.localePublishes?.length) return item.localePublishes;
  return item.locales.map((locale) => ({
    locale,
    published: false,
    kind: "unpublished" as const,
    groupIds: [],
    publishGroups: [],
  }));
}

function LocalePublishBadges({ item }: { item: CaseSummary }) {
  const locales = localePublishesForCase(item);
  if (locales.length === 0) {
    return <span className="text-muted-foreground">-</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1">
      {locales.map((locale) => (
        <Tooltip key={locale.locale}>
          <TooltipTrigger
            render={
              <Badge
                variant={localePublishBadgeVariant(locale)}
                className="cursor-default"
              />
            }
          >
            {locale.locale}
          </TooltipTrigger>
          <TooltipContent>{localePublishTooltip(locale)}</TooltipContent>
        </Tooltip>
      ))}
    </div>
  );
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("zh-CN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function CasesTableSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <div className="flex flex-col gap-0">
        <div className="flex gap-4 border-b border-border px-4 py-3">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-4 w-24" />
        </div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="flex items-center gap-4 border-b border-border px-4 py-3 last:border-0"
          >
            <Skeleton className="h-4 flex-1" />
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-28" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CasesPage() {
  const auth = useAuth();
  const casesQuery = useQuery({
    queryKey: ["cases"],
    queryFn: () => api.listCases(),
    enabled: auth.status === "authed",
  });

  if (auth.status === "loading") {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-72" />
        </div>
        <CasesTableSkeleton />
      </div>
    );
  }

  if (auth.status === "anon") {
    return null;
  }

  const cases = casesQuery.data?.cases ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="font-heading text-2xl font-semibold tracking-tight">
            案件
          </h1>
          <p className="text-sm text-muted-foreground">
            从剧本文本创建案件，审阅元信息，再运行制作流水线。
          </p>
        </div>
        {casesQuery.isSuccess && cases.length > 0 ? (
          <Button render={<Link to="/cases/new" />} nativeButton={false}>
            <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" />
            创建案件
          </Button>
        ) : null}
      </div>

      {casesQuery.isLoading && <CasesTableSkeleton />}

      {casesQuery.isError && (
        <p className="text-sm text-destructive" role="alert">
          无法加载案件，请刷新重试。
        </p>
      )}

      {casesQuery.isSuccess && cases.length === 0 && (
        <Empty className="border border-border">
          <EmptyHeader>
            <EmptyTitle>暂无案件</EmptyTitle>
            <EmptyDescription>
              粘贴案件剧本以创建草稿。接着审阅元信息，再启动流水线。若源文件是
              PDF 或图片，请先使用提取。
            </EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button render={<Link to="/cases/new" />} nativeButton={false}>
              <HugeiconsIcon icon={Add01Icon} data-icon="inline-start" />
              创建案件
            </Button>
          </EmptyContent>
        </Empty>
      )}

      {casesQuery.isSuccess && cases.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>案件</TableHead>
                <TableHead>发布</TableHead>
                <TableHead>版本</TableHead>
                <TableHead>更新时间</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Link
                      to="/cases/$caseId/versions"
                      params={{ caseId: item.id }}
                      className="font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      {item.title || item.id}
                    </Link>
                    {item.title ? (
                      <p className="text-xs text-muted-foreground">
                        {item.id}
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <LocalePublishBadges item={item} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {item.latestVersion
                      ? `v${item.latestVersion.versionN} · ${item.latestVersion.status}`
                      : "-"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatWhen(item.updatedAt)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
